import axios from "axios";

export const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
export const API = `${BACKEND_URL}/api`;

const client = axios.create({ baseURL: API });

// attach admin token if present
client.interceptors.request.use((config) => {
  const token = localStorage.getItem("ekta_admin_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

export default client;

// Normalise a slug for links (strip any accidental path so /news/<slug> never doubles up).
export function cleanSlug(s) {
  if (!s) return "";
  return String(s).trim().replace(/^\/+/, "").split("/").filter(Boolean).pop() || "";
}

// Render either stored rich HTML or plain text (with paragraph/line breaks) safely.
export function toRichHtml(text) {
  if (!text) return "";
  const t = String(text);
  if (/<[a-z][\s\S]*>/i.test(t)) return t; // already HTML
  return t.split(/\n\n+/).map((p) => `<p>${p.replace(/\n/g, "<br/>")}</p>`).join("");
}

// Resolve an image url: backend media paths -> absolute; external URLs untouched
export function img(url) {
  if (!url) return "";
  if (url.startsWith("/api/")) return `${BACKEND_URL}${url}`;
  return url;
}

// Backend media supports on-the-fly responsive resizing via ?w=. External URLs are returned as-is.
export function imgVariant(url, w) {
  if (!url) return "";
  const isMedia = url.startsWith("/api/") || url.includes("/api/media/");
  const abs = img(url);
  if (!isMedia || !w) return abs;
  return `${abs}${abs.includes("?") ? "&" : "?"}w=${w}`;
}

// Build a srcSet string for a backend media url across common widths.
export function imgSrcSet(url, widths = [400, 800, 1200, 1600]) {
  if (!url) return "";
  const isMedia = url.startsWith("/api/") || url.includes("/api/media/");
  if (!isMedia) return "";
  return widths.map((w) => `${imgVariant(url, w)} ${w}w`).join(", ");
}

// ---- public helpers ----
export const getBootstrap = () => client.get("/bootstrap").then((r) => r.data);
export const getCollection = (name, all = false) => client.get(`/collection/${name}`, { params: { all } }).then((r) => r.data);
export const getProducts = (category) => client.get("/products", { params: category ? { category } : {} }).then((r) => r.data);
export const getProductBySlug = (slug) => client.get(`/products/slug/${slug}`).then((r) => r.data);
export const getCategoryBySlug = (slug) => client.get(`/categories/slug/${slug}`).then((r) => r.data);
export const getSingleton = (key) => client.get(`/singleton/${key}`).then((r) => r.data);
export const getNewsBySlug = (slug) => client.get(`/collection/news/slug/${slug}`).then((r) => r.data);
export const getSeo = (key) => client.get(`/seo/${key}`).then((r) => r.data);
export const postEnquiry = (payload) => client.post("/enquiries", payload).then((r) => r.data);
export const postChat = (session_id, message) => client.post("/ai/chat", { session_id, message }).then((r) => r.data);
export const getReviews = () => client.get("/reviews").then((r) => r.data);

// ---- admin helpers ----
export const adminLogin = (email, password) => client.post("/admin/login", { email, password }).then((r) => r.data);
export const adminMe = () => client.get("/admin/me").then((r) => r.data);
export const adminGetCollection = (name) => client.get(`/collection/${name}`, { params: { all: true } }).then((r) => r.data);
export const adminCreate = (name, payload) => client.post(`/admin/collection/${name}`, payload).then((r) => r.data);
export const adminUpdate = (name, id, payload) => client.put(`/admin/collection/${name}/${id}`, payload).then((r) => r.data);
export const adminDelete = (name, id) => client.delete(`/admin/collection/${name}/${id}`).then((r) => r.data);
export const adminReorder = (name, ids) => client.post(`/admin/collection/${name}/reorder`, { ids }).then((r) => r.data);
export const adminPutSingleton = (key, payload) => client.put(`/admin/singleton/${key}`, payload).then((r) => r.data);
export const adminGetProducts = () => client.get("/products", { params: { all: true } }).then((r) => r.data);
export const adminGetSeoHealth = () => client.get("/admin/seo/health").then((r) => r.data);
export const adminListEnquiries = () => client.get("/admin/enquiries").then((r) => r.data);
export const adminUpdateEnquiry = (id, payload) => client.put(`/admin/enquiries/${id}`, payload).then((r) => r.data);
export const adminDeleteEnquiry = (id) => client.delete(`/admin/enquiries/${id}`).then((r) => r.data);
export const adminListSeo = () => client.get("/admin/seo").then((r) => r.data);
export const adminPutSeo = (key, payload) => client.put(`/admin/seo/${key}`, payload).then((r) => r.data);
export const adminListMedia = () => client.get("/admin/media").then((r) => r.data);
export const adminUploadMedia = (file, alt = "", caption = "") => {
  const fd = new FormData();
  fd.append("file", file);
  fd.append("alt", alt);
  fd.append("caption", caption);
  return client.post("/admin/media", fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
};
export const adminDeleteMedia = (id) => client.delete(`/admin/media/${id}`).then((r) => r.data);
